import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { logger } from '../lib/utils/logger';
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  refreshAccessToken,
} from '../lib/services/authApi';
import { AUTH_MESSAGES, USER_ROLES } from '../constants/authMessages';
import { showWarning } from '../lib/services/errorService';

/**
 * Authentication context.
 * Uses httpOnly cookies — no localStorage token checks needed.
 */
export const AuthContext = createContext(null);

/**
 * AuthProvider component.
 * On mount, calls getCurrentUser() to check if the browser has a valid session cookie.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize auth by calling getCurrentUser().
   * The httpOnly cookie is sent automatically — no localStorage check needed.
   * If the cookie is expired, getCurrentUser() internally attempts one refresh.
   */
  const initializeAuth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (err) {
      logger.debug('No active session:', err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Periodic token refresh (every 10 minutes while logged in)
  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(
      () => {
        refreshAccessToken().catch(() => {
          setUser(null);
          toast.warning(AUTH_MESSAGES.ERROR_SESSION_EXPIRED);
        });
      },
      10 * 60 * 1000
    ); // Every 10 minutes

    return () => clearInterval(intervalId);
  }, [user]);

  /**
   * Login with credentials
   */
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiLogin(email, password);
      setUser(response.user);
      toast.success(AUTH_MESSAGES.SUCCESS_LOGIN);
      return response.user;
    } catch (err) {
      const errorMessage =
        err.status === 401
          ? AUTH_MESSAGES.ERROR_INVALID_CREDENTIALS
          : err.status === 403
            ? AUTH_MESSAGES.ERROR_ACCOUNT_DISABLED
            : AUTH_MESSAGES.ERROR_NETWORK;

      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout current user
   */
  const logout = useCallback(async () => {
    try {
      await apiLogout();
      toast.success(AUTH_MESSAGES.SUCCESS_LOGOUT);
    } catch (err) {
      logger.error('Logout error:', err);
      showWarning('서버 세션 종료에 실패했습니다. 로컬 세션은 정리되었습니다.');
    } finally {
      sessionStorage.clear();
      setUser(null);
      setError(null);
    }
  }, []);

  const hasRole = useCallback(
    (roles) => {
      if (!user) return false;
      const roleArray = Array.isArray(roles) ? roles : [roles];
      return roleArray.includes(user.role);
    },
    [user]
  );

  const isGlobalAdmin = useCallback(() => {
    return hasRole(USER_ROLES.GLOBAL_ADMIN);
  }, [hasRole]);

  const isTenantAdminOrHigher = useCallback(() => {
    return hasRole([USER_ROLES.GLOBAL_ADMIN, USER_ROLES.TENANT_ADMIN]);
  }, [hasRole]);

  const canAccessTenant = useCallback(
    (tenantId) => {
      if (!user) return false;
      if (isGlobalAdmin()) return true;
      return user.tenantId === tenantId;
    },
    [user, isGlobalAdmin]
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: !!user,
      login,
      logout,
      hasRole,
      isGlobalAdmin,
      isTenantAdminOrHigher,
      canAccessTenant,
      refreshAuth: initializeAuth,
    }),
    [
      user,
      loading,
      error,
      login,
      logout,
      hasRole,
      isGlobalAdmin,
      isTenantAdminOrHigher,
      canAccessTenant,
      initializeAuth,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
